/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import React, { Suspense, lazy, useCallback, useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from "@i18n";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "@Reducers/index";
import { AiOutlineLeft } from "react-icons/ai";
import { PredicateBuilder, PredicateShape } from "@Services/queryBuilder";
import ViewIssue from "../Issue/viewIssue";
import { CubeGlobal, HoopsViewerContext } from "@Features/viewer-core";
import {
  getDefaultListOfStatusAvailable,
  getPdfMarkUpData,
  getfilteredIssuesData,
  isIssueRequiredPermission,
  issuesBcfImport,
} from "@Features/issues/state/bcfData.actions";
import { deleteIssueData } from "@Features/issues/state/deletingIssues.actions";
import { deleteIssuesPinData } from "@Features/issues/state/issuesPinDataSlice";
import { addAnnotation, setAnnotations, setModalStatus, updateViewerState } from "../../../../reducers/Viewer";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import IconComponent from "@CubeComponents/icons";
import RadioButton from "@CubeComponents/Mui_Form/RadioButton";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { BIM_ENGINE, CAD_ENGINE, PDF_ENGINE, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { cubeView } from "@Services/cubeView";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
import ExportPdfOptionsModal from "@CubeComponents/form/PaperSizeSelector/ExportPdfOptionsModal";
import type { PaperSize } from "@CubeComponents/form/PaperSizeSelector/PaperSizeSelector";
import _ from "lodash";
import { fileToBase64, getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { showToastNotification } from "@Reducers/toastSlice";
import { useLocation } from "react-router-dom";
import cloneObject from "@Services/cloneObject";
import { emptyIssuesData } from "@Features/issues/state/issues";
import Text from "@CubeComponents/html/Text/Text";
import { DisabledTooltip } from "@Components/DisabledTooptip";
import { createUuid } from "@ts3d-hoops/web-viewer";
import { folderPermissions } from "@Helpers/constants";
import type {
  IssueItem,
  GetPdfMarkUpDataRequest,
  GetIssuesDataRequest,
  SaveIssuesDataFromBCFRequest,
} from "@ServerTypes/issues/issues.schema";
import type { ReviewStatusItem } from "@ServerTypes/reviews/reviews.schema";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";

const IssuesList = lazy(() => import("../IssuesList/IssuesList"));
const CreateIssue = lazy(() => import("../CreateIssue/CreateIssue"));

/**
 * The BCF objects this pallet reads out of the viewer. HOOPS ships declarations for all of them
 * (`Bcf.BCFTopic`, `BCFMarkup`, `BCFComment`, `BCFSnapshot`, `BCFViewpoint`), but every field read
 * below — `_text`, `_author`, `_date`, `_viewpointGuid`, `_viewpointFilename`, `_snapshotFilename`,
 * `_data`, `_viewer` — is `private` on those classes and therefore unreachable through any erased
 * assertion, so the shapes are described structurally here. The values themselves arrive as `any`
 * through `HoopsViewerImpl`'s `[key: string]: any` index signature. Method return types match the
 * shipped declarations exactly, including the `Map`s noted at the loops below.
 */
interface BcfViewpointData {
  /** Stripped by `getbcfData` before serialising; optional so the `cloneObject` result that
   *  function reassigns into its own parameter still fits the type. */
  _viewer?: unknown;
  getCamera(): { toJson(): unknown } | null;
}

interface BcfSnapshotData {
  _data: Uint8Array;
}

interface BcfMarkupViewpointData {
  _viewpointFilename: string;
  _snapshotFilename: string;
}

interface BcfCommentData {
  _text: string;
  _author: string;
  _date: Date;
  _viewpointGuid: string;
}

interface BcfMarkupData {
  getComments(): Map<string, BcfCommentData>;
  getViewpoints(): Map<string, BcfMarkupViewpointData>;
  getTopicCreationAuthor(): string;
  getTopicCreationDate(): Date;
  getTopicDescription(): string | null;
  getTopicTitle(): string;
  getTopicStatus(): string | null;
  getTopicDueDate(): Date | null;
}

interface BcfTopicData {
  getMarkup(): BcfMarkupData;
  getSnapshot(filename: string): BcfSnapshotData | null;
  getViewpoint(filename: string): BcfViewpointData | null;
}

/** One comment as the BCF parser assembles it for import. */
interface BcfImportComment {
  title: string;
  createdBy: string;
  createdDate: Date;
  snapshot: string | null;
  viewpoint: string | null;
  bcfData: string | null;
}

/**
 * One issue as the BCF parser assembles it. Declared standalone rather than derived from
 * `SaveIssuesDataFromBCFRequest["issuesList"][number]` because the parsed values differ from what
 * that schema declares: `dueDate` is a `Date` (the schema wants a date STRING), `description` /
 * `snapshot` / `viewpoint` / `bcfData` can be `null`, and `createdBy`/`createdDate` — on the issue
 * and on each comment — are not declared at all. SCHEMA GAP: the import service ignores the
 * undeclared fields, so the payload is preserved verbatim and asserted at the request boundary.
 */
interface BcfImportIssue {
  createdBy: string;
  createdDate: Date;
  description: string | null;
  title: string;
  status: string | undefined;
  dueDate: Date | null;
  comments: BcfImportComment[];
  snapshot?: string | null;
  viewpoint?: string | null;
  bcfData?: string | null;
}

/** The axios envelope `getDefaultListOfStatusAvailable` resolves to. */
type DefaultStatusResponse = Awaited<ReturnType<typeof getDefaultListOfStatusAvailable>>;

/**
 * The one markup internal the delete path matches on. `_uniqueId` is not a member of any HOOPS
 * markup class — see the KNOWN BUG note at `handleIssueDelete`.
 */
interface PinMeasureMarkup {
  _uniqueId: string;
}

interface IssuesPalletProps {
  handleExportToBcf: (data: { issues: RootState["issues"]; selectedIssues: Record<string, boolean> }) => Promise<void>;
  onViewIssueClick: (issueId: string) => void;
  handleSelectedIssues: (issueId: string, deleteAll?: boolean) => void;
  fileType: string;
  engineType: string;
}

function IssuesPallet({
  handleExportToBcf,
  onViewIssueClick,
  handleSelectedIssues,
  fileType,
  engineType,
}: Readonly<IssuesPalletProps>) {
  const { t } = useTranslation();
  const HoopsModalStatus = useSelector((state: RootState) => state.HoopsModalStatus);
  const dispatch = useDispatch<AppDispatch>();
  const {
    fileVersionId: contextFileVersionId,
    fileVersionData,
    selectedIssues,
    currentViewIssueId,
    transmittalVersionId,
    isModelStructureReady,
    viewerImpl,
    isSettingsApplied,
  } = useContext(HoopsViewerContext);
  // `HoopsViewerContext.fileVersionId` is optional ONLY so the default context (used outside a
  // Provider) can have no file; this pallet is mounted exclusively by Viewer.tsx and DesignViewer.tsx,
  // both of which always put the open file's id on the provider value. Erasing it once here rather
  // than at each of the ~20 request sites below. See the blocked note: the honest fix is for the
  // viewer to hand this pallet a required `fileVersionId` prop instead of reading the context.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- optional only on the no-file default context; see comment above
  const fileVersionId = contextFileVersionId as ObjectIdString;
  const [searchIssue, setSearchIssue] = useState<boolean>(false);
  const [showFilterIssue, setShowFilterIssue] = useState<boolean>(false);
  const [searchString, setSearchString] = useState<PredicateShape | Record<string, never>>({});
  const [filterPredicates, setFilterPredicates] = useState<PredicateShape | Record<string, never>>({});
  const [filterConditions, setFilterConditions] = useState<Record<string, string>>({});
  const [deleteEnabled, setDeleteEnabled] = useState<boolean>(true);
  const [openDeleteModalPopUp, setOpenDeleteModalPopUp] = useState<boolean>(false);
  const [openExportModalPopUp, setOpenExportModalPopUp] = useState<boolean>(false);
  const [sortCondition, setSortCondition] = useState<boolean>(false);
  const [sortPredicates, setSortPredicates] = useState<{ name: string; direction: string }[]>([]);
  const [handleExportBcfBtn, setHandleExportBcfBtn] = useState<boolean>(false);
  const [viewPdfMarkupToggle, setViewPdfMarkupToggle] = useState<boolean>(false);
  const [pdfMarkupData, setPdfMarkupData] = useState<IssueItem[]>([]);
  const [disableImportButton, setDisableImportButton] = useState<boolean>(false);
  const [selectedIssuesPermissions, setSelectedIssuesPermission] = useState({
    delete: true,
    export: true,
  });
  const issues = useSelector((state: RootState) => state.issues);

  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const location = useLocation();
  const urlSearchParams = new URLSearchParams(location.search);
  // `ViewIssue.spaceId` is a REQUIRED branded id, while both sources here are optional: the query
  // param is client-supplied (so it is parsed, not asserted) and `fileVersionData` is `Partial` on
  // the viewer context. Without either, ViewIssue has no Space to load the issue's review flow from,
  // so the panel is not mounted at all rather than being handed an `undefined` it would fail on.
  const spaceIdParam = objectIdString.safeParse(urlSearchParams.get("spaceId"));
  const viewIssueSpaceId: ObjectIdString | undefined = spaceIdParam.success
    ? spaceIdParam.data
    : fileVersionData?.spaceId;
  const { RESOURCE_DELETE, RESOURCE_DOWNLOAD } = folderPermissions();

  useEffect(() => {
    dispatch(emptyIssuesData());
    handleSelectedIssues("removeAll", true);
  }, []);

  useEffect(
    () => () => {
      removeMarkupHandler();
    },

    [],
  );

  const removeMarkupHandler = () => {
    if (
      [BIM_ENGINE, CAD_ENGINE].includes(engineType) &&
      getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })
    ) {
      const activeMarkups = viewerImpl?.hwv.markupManager.getActiveMarkupView(viewerImpl.hwv.view);
      if (activeMarkups) {
        viewerImpl?.hwv.markupManager.deleteMarkupView(activeMarkups._uniqueId);
      }
      cubeView.flushView();
    }
  };

  useEffect(() => {
    if (
      [BIM_ENGINE, CAD_ENGINE].includes(engineType) &&
      HoopsModalStatus.Issues?.show &&
      HoopsModalStatus.Issues.currentStage == HoopsModalStatus.Issues.defaultStage
    ) {
      removeMarkupHandler();
    }
    // Only the Issues entry is read. Depending on the whole modal slice deleted the active markup
    // view whenever any other viewer modal was opened or closed.
  }, [HoopsModalStatus.Issues]);

  useEffect(() => {
    if (sortCondition) {
      setSortPredicates([{ name: "createdDate", direction: "ascending" }]);
    } else {
      setSortPredicates([{ name: "createdDate", direction: "descending" }]);
    }
  }, [sortCondition]);

  useEffect(() => {
    let allowDelete = true;
    let allowDownload = true;
    for (const issueId of Object.keys(selectedIssues)) {
      if (issues[issueId]?.issueType === "external") {
        setDeleteEnabled(false);
        break;
      }

      if (!isIssueRequiredPermission(issues[issueId], RESOURCE_DELETE.id)) {
        allowDelete = false;
      }
      if (!isIssueRequiredPermission(issues[issueId], RESOURCE_DOWNLOAD.id)) {
        allowDownload = false;
      }
    }
    setSelectedIssuesPermission((prev) => ({ ...prev, delete: allowDelete, export: allowDownload }));
  }, [issues, selectedIssues, RESOURCE_DELETE.id, RESOURCE_DOWNLOAD.id]);

  const handleSetFilterConditions = (e: React.ChangeEvent<HTMLInputElement>) => {
    const filterData: Record<string, string> = { ...filterConditions };
    if (e.target.value) {
      filterData[e.target.name] = e.target.value;
    } else {
      delete filterData[e.target.name];
    }
    setFilterConditions(filterData);
  };

  const handleOpenCreateIssue = () => {
    dispatch(
      setModalStatus({
        ...HoopsModalStatus,
        Issues: {
          ...HoopsModalStatus.Issues,
          currentStage: "create_issue",
          backStage: HoopsModalStatus.Issues?.defaultStage,
        },
      }),
    );
  };

  const onInputChangePred = () => {
    if (Object.keys(filterConditions).length) {
      let predicate: PredicateBuilder | undefined;
      for (const filterKey of Object.keys(filterConditions)) {
        predicate = predicate
          ? predicate.and(filterKey, "contains", filterConditions[filterKey])
          : new PredicateBuilder(filterKey, "contains", filterConditions[filterKey]);
      }
      if (predicate) setFilterPredicates(predicate);
    }
  };

  const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
    const predicate = new PredicateBuilder("title", "contains", e.target.value);
    setSearchString(predicate);
  };

  const handleResetSearch = () => {
    setSearchString({});
    setSearchIssue(false);
  };

  const handleData = useCallback(async () => {
    const params: GetPdfMarkUpDataRequest = {
      fileId: [fileVersionId],
    };
    const pdfMarkupData = await getPdfMarkUpData(params);
    if (pdfMarkupData.data.status) {
      setPdfMarkupData(pdfMarkupData.data.result!);
    }
  }, [fileVersionId]);

  const issuesStage = HoopsModalStatus.Issues?.currentStage;

  useEffect(() => {
    if ([PDF_ENGINE].includes(engineType) && issuesStage === "issues") {
      handleData();
    }
  }, [engineType, issuesStage, handleData]);

  const handleIssueDelete = () => {
    if ([BIM_ENGINE, CAD_ENGINE].includes(engineType)) {
      const measurementIds: string[] = [];
      const allMeasurementsInViewer = viewerImpl?.hwv.measureManager.getAllMeasurements();
      for (const issueId of Object.keys(selectedIssues)) {
        const issue = issues[issueId];
        // `pinData` is nullable AND optional on `IssueItem`; an issue without one used to reach
        // `JSON.parse(undefined)` and throw, taking the whole delete with it.
        if (issue?.pinData && issue.pinData != "{}") {
          measurementIds.push(JSON.parse(issue.pinData)._uniqueId);
        }
      }
      // KNOWN BUG, preserved verbatim: `_uniqueId` exists only on the serialised `pinData` JSON —
      // `CustomPinMarkup.fromJson` copies it onto `cubeUniqueId` — so a live markup has no
      // `_uniqueId` and this filter always yields an empty list, leaving the pin in the viewer.
      // Typed, not corrected: reading `cubeUniqueId` instead would change behaviour.
      const markupsToRemove = allMeasurementsInViewer.filter((record: PinMeasureMarkup) =>
        measurementIds.includes(record._uniqueId),
      );
      for (const markup of markupsToRemove) {
        viewerImpl?.hwv.measureManager.removeMeasurement(markup);
      }
      dispatch(deleteIssuesPinData(Object.keys(selectedIssues)));
    } else {
      setPdfMarkupData((prev) => prev.filter((data) => !Object.keys(selectedIssues).includes(data.id!)));
    }
    // `selectedIssues` is keyed by issue id and `Object.keys()` erases key types, so the ids are
    // PARSED before they enter a branded request parameter rather than asserted across.
    const selectedIssueIds = Object.keys(selectedIssues).flatMap((issueId) => {
      const parsed = objectIdString.safeParse(issueId);
      return parsed.success ? [parsed.data] : [];
    });
    dispatch(deleteIssueData(selectedIssueIds));
    for (const issueId of Object.keys(selectedIssues)) {
      handleSelectedIssues(issueId);
    }
    setOpenDeleteModalPopUp(false);
  };

  const closeDeleteModal = () => {
    setOpenDeleteModalPopUp(false);
  };

  const handleDeleteButton = () => {
    setOpenDeleteModalPopUp(true);
  };

  const closeExportModal = () => {
    setOpenExportModalPopUp(false);
  };

  const handleExportButton = () => {
    setOpenExportModalPopUp(true);
  };

  const handleImportBCFButtonClick = () => {
    // The hidden `<input type="file">` is mounted whenever this button is, so the ref is set.
    inputFileRef.current!.click();
  };

  const handleExport = async () => {
    setHandleExportBcfBtn(true);
    await handleExportToBcf({ issues, selectedIssues });
    setHandleExportBcfBtn(false);
    closeExportModal();
  };

  const [showExportPdfOptions, setShowExportPdfOptions] = useState(false);

  const handleExportToPdfButton = async () => {
    setShowExportPdfOptions(true);
  };

  const handleExportToPdfConfirm = (paperSize: PaperSize) => {
    setShowExportPdfOptions(false);
    const issuesIds: string[] = Object.keys(selectedIssues);
    exportPdf({ issuesIds, paperSize, exportType: EXPORT_TYPE.PDF });
  };

  const showPdfMarkupsHandler = (args: React.ChangeEvent<HTMLInputElement>) => {
    // TODO: pradeep Quick patch as delete anotations are crashing again
    try {
      if (args.target.checked) {
        dispatch(updateViewerState({ field: "showAllIssueMarkups", value: true }));
        pdfMarkupData.map((markup) => {
          dispatch(addAnnotation({ data: JSON.parse(markup.viewpoint!), annotationType: "markupAnnotations" }));
        });
      } else {
        dispatch(updateViewerState({ field: "showAllIssueMarkups", value: false }));
        dispatch(setAnnotations({ data: [], annotationType: "markupAnnotations" }));
      }
      setViewPdfMarkupToggle(args.target.checked);
    } catch (e) {
      console.log(e);
      Promise.reject(e);
    }
  };

  const deletePopup = () => (
    <div className="flex flex-col gap-10">
      <ModalStateDialogComponent
        footerContent={
          <div className="flex w-full justify-between">
            <ButtonComponent onClick={closeDeleteModal} buttonType={BUTTON_TYPES.BORDER_BUTTON}>
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
            <ButtonComponent onClick={handleIssueDelete} buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
              <Text>{t("Delete")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <span className="">
            <Text>{t("Delete all selected?")}</Text>
          </span>
        }
        isVisible={openDeleteModalPopUp}
        onCancel={closeDeleteModal}
        modelContent={
          <Text sx={{ whiteSpace: "normal" }}>{t("Are you sure you want to delete the selected items?")}</Text>
        }
        width="23.125rem"
      />
    </div>
  );

  const exportPopUp = () => (
    <div>
      <ModalStateDialogComponent
        footerContent={
          <div className="flex w-full justify-between">
            <ButtonComponent
              onClick={closeExportModal}
              buttonType={BUTTON_TYPES.BORDER_BUTTON}
              disabled={handleExportBcfBtn}
            >
              <Text>{t("Cancel")}</Text>
            </ButtonComponent>
            <ButtonComponent
              onClick={handleExport}
              buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              disabled={handleExportBcfBtn}
            >
              <Text>{t("Export")}</Text>
            </ButtonComponent>
          </div>
        }
        headerTitle={
          <span className="">
            <Text>{t("Export all selected as")}</Text>
          </span>
        }
        onCancel={closeExportModal}
        isVisible={openExportModalPopUp}
        modelContent={
          <div className="flex flex-col gap-8">
            <p>
              <Text>{t("Please select the format in which you want to export the selected issues")}</Text>
            </p>
            <div className="flex flex-col gap-2.5">
              <div>
                <RadioButton label="BCF" name="export" />
              </div>
              <div>
                <RadioButton label="BCFzip" name="export" />
              </div>
            </div>
          </div>
        }
        width="25rem"
      />
    </div>
  );

  const viewpointConvertorForBCF = (viewpointMap: BcfViewpointData | null) => {
    if (!viewpointMap) {
      return null;
    }
    const viewpoint = {
      // KNOWN BUG, preserved verbatim: `getCamera()` returns `Camera | null` on the shipped
      // declaration and is dereferenced with no guard. Erased assertion; a `?.` would change behaviour.
      camera: viewpointMap.getCamera()!.toJson(),
      markup: [],
      cuttingData: {
        cappingFaceColor: { r: 128, g: 128, b: 128 },
        cappingGeometryVisibility: true,
        cappingLineColor: { r: 128, g: 128, b: 128 },
        cuttingSections: [],
        pickable: true,
      },
      colors: [],
      sheetId: null,
      explodeMagnitude: 0,
      lineVisibility: true,
      faceVisibility: true,
      defaultVisibility: true,
      visibilityExceptions: [],
      measurementsMarkup: [],
      selection: [],
      shading: 2,
      name: "",
      uniqueId: createUuid(),
      transparencies: { elements: [], value: 0.4 },
      modelTransfroms: [],
    };

    return JSON.stringify(viewpoint);
  };

  // `Partial<>` because the `cloneObject` result reassigned into `bcfData` below is a bare object.
  const getbcfData = (bcfData: Partial<BcfViewpointData> | null) => {
    if (!bcfData) {
      return null;
    }

    delete bcfData._viewer;
    bcfData = cloneObject(bcfData);
    return JSON.stringify(bcfData);
  };

  const snapshotConvertorForBCF = (snapshotunit8Array: Uint8Array | undefined) => {
    if (!snapshotunit8Array) {
      return null;
    }
    let binaryString = "";
    for (const byte of snapshotunit8Array) {
      binaryString += String.fromCodePoint(byte);
    }
    const abc = btoa(binaryString);
    return `data:image/png;base64,${abc}`;
  };

  const commentsConvertorForBCF = (topicMarkup: BcfMarkupData, topic: BcfTopicData) => {
    const comments = topicMarkup.getComments();
    const viewpointMap = topicMarkup.getViewpoints();
    const commentsList: BcfImportComment[] = [];
    // `getComments()` returns a `Map`, so `for…of` yields `[id, comment]` ENTRY TUPLES - the value
    // has to be destructured out. It used to iterate the tuples directly and read the comment fields
    // off them through assertions, which made every imported comment's text/author/date undefined.
    for (const [, comment] of comments) {
      const viewpointfileName = viewpointMap.get(comment._viewpointGuid)?._viewpointFilename;
      const snapshotfileName = viewpointMap.get(comment._viewpointGuid)?._snapshotFilename;
      commentsList.push({
        title: comment._text,
        createdBy: comment._author,
        createdDate: comment._date,
        // Both file names are `string | undefined` (the viewpoint lookup can miss); unguarded here.
        snapshot: snapshotConvertorForBCF(topic.getSnapshot(snapshotfileName!)?._data),
        viewpoint: viewpointConvertorForBCF(topic.getViewpoint(viewpointfileName!)),
        bcfData: getbcfData(topic.getViewpoint(viewpointfileName!)),
      });
    }
    return commentsList;
  };

  // `name` is widened to include `null` because `getTopicStatus()` returns `string | null`.
  const getIssueStatusForBCF = ({ name, statusList }: { name: string | null; statusList: ReviewStatusItem[] }) => {
    let mappedValue: string | undefined;
    const statusMapper = [
      {
        key: ["in_review", "in review", "in_progress", "in progress"],
        value: "In Review",
      },
      {
        key: ["open", "active"],
        value: "Open",
      },
      {
        key: ["closed", "completed"],
        value: "Closed",
      },
    ];

    if (name) {
      name = name.toLowerCase();
      for (const { key, value } of statusMapper) {
        if (key.includes(name)) {
          mappedValue = value;
        }
      }
    }
    if (!mappedValue) {
      mappedValue = "Open";
    }
    // KNOWN BUG, preserved verbatim: `find` can return `undefined` (the mapped value need not be in
    // the space's status list) and is dereferenced with no guard. Erased assertion.
    return statusList.find((status: ReviewStatusItem) => status.name == mappedValue)!._id;
  };

  const importIssues = async () => {
    // Query params are client-supplied, so the Space id is PARSED once here and reused below rather
    // than asserted into a branded request parameter at each read.
    const parsedSpaceId = objectIdString.safeParse(urlSearchParams.get("spaceId"));
    if (!parsedSpaceId.success) return;
    const spaceId = parsedSpaceId.data;
    // `statusList` starts as the response envelope and is then reassigned to the status array it
    // carries, so it holds either shape; each read asserts the one it expects. Erased.
    let statusList: DefaultStatusResponse | ReviewStatusItem[] = await getDefaultListOfStatusAvailable({
      spaceId,
      entityType: "ISSUES",
    });
    if (
      !(statusList as DefaultStatusResponse).data.status ||
      !(statusList as DefaultStatusResponse).data.result!.length
    ) {
      dispatch(
        showToastNotification({
          content: t("Status not found!"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      return;
    }
    statusList = (statusList as DefaultStatusResponse).data.result!;
    const importIssuesData: BcfImportIssue[] = [];
    const data = viewerImpl?.hwv.BCFManager.getBCFMap();
    for (const mappedData of data) {
      const allTopics = mappedData.getTopics();
      for (const topic of allTopics) {
        const topicMarkup = topic.getMarkup();
        importIssuesData.push({
          createdBy: topicMarkup.getTopicCreationAuthor(),
          createdDate: topicMarkup.getTopicCreationDate(),
          description: topicMarkup.getTopicDescription(),
          // modifiedDate : topicMarkup.getTopicModifiedDate(),
          // modifiedBy : topicMarkup.getTopicModifiedAuthor(),
          title: topicMarkup.getTopicTitle(),
          status: getIssueStatusForBCF({
            name: topicMarkup.getTopicStatus(),
            statusList,
          }),
          dueDate: topicMarkup.getTopicDueDate(),
          comments: commentsConvertorForBCF(topicMarkup, topic),
        });

        // `at(-1)` is the element pushed on the line above, so it is always present here.
        importIssuesData.at(-1)!.snapshot = snapshotConvertorForBCF(topic.getSnapshot("snapshot.png")?._data);
        importIssuesData.at(-1)!.viewpoint = viewpointConvertorForBCF(topic.getViewpoint("viewpoint.bcfv"));
        importIssuesData.at(-1)!.bcfData = getbcfData(topic.getViewpoint("viewpoint.bcfv"));
      }
    }

    const params = {
      // See `BcfImportIssue`: the assembled payload carries fields the request schema does not
      // declare and types `dueDate`/`createdDate` as `Date`. `Exact<>` rejects both deeply, so the
      // payload is preserved verbatim and reached through an array-level intersection here plus a
      // single assertion at the call below, rather than by reshaping what the parser produced.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see `BcfImportIssue`: reshaping is a schema change, tracked as blocked
      issuesList: importIssuesData as typeof importIssuesData & SaveIssuesDataFromBCFRequest["issuesList"],
      fileId: fileVersionId,
      spaceId,
    };
    const response = await issuesBcfImport(params as SaveIssuesDataFromBCFRequest);
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("BCF has been imported successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      const getParams: GetIssuesDataRequest = {
        fileId: [fileVersionId],
        value: {
          skip: 0,
          take: 5,
          requiresCounts: true,
        },
      };
      dispatch(getfilteredIssuesData(getParams));
    }
    for (const [key] of data) {
      viewerImpl?.hwv.BCFManager.removeBCFData(key);
    }
  };

  const handleBcfImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisableImportButton(true);
    try {
      const file = e.target.files?.[0];
      if (!file) return;
      const fileBase64Str = await fileToBase64(file);
      await viewerImpl?.hwv.BCFManager.addBCFFromFile(fileBase64Str);
      await importIssues();
    } catch (e) {
      console.log(e);
      dispatch(
        showToastNotification({
          content: t("Invalid Format!"),
          type: TOAST_TYPE_ERROR,
        }),
      );
      Promise.reject(e);
    } finally {
      inputFileRef.current!.value = "";
      setDisableImportButton(false);
    }
  };

  const handleSortClick = () => {
    setSortCondition((prev) => !prev);
  };

  const resetFilterHandler = () => {
    setShowFilterIssue(false);
    setFilterPredicates({});
    setFilterConditions({});
  };

  const viewerLoaded = getIsViewerDependencyLoaded({
    isModelStructureReady,
    isSettingsApplied,
    viewerImpl,
    hwv: CubeGlobal.hwv,
  });
  return (
    <div className="flex h-full bg-white overflow-y-auto flex-col">
      {deletePopup()}
      {exportPopUp()}
      {showExportPdfOptions && (
        <ExportPdfOptionsModal
          isVisible
          onExport={handleExportToPdfConfirm}
          onCancel={() => setShowExportPdfOptions(false)}
        />
      )}
      {HoopsModalStatus.Issues?.currentStage === "issues" && (
        <div
          className="flex min-h-8 flex-col bg-white p-4 shadow-[0_0_0.25rem_0_rgba(0,0,0,0.15)]"
          data-testid="palletHeader"
        >
          <div className="w-full flex items-center gap-2.5" data-testid="palletOptions">
            {Object.keys(selectedIssues).length === 0 && !searchIssue && (
              <div className="flex justify-between w-full">
                <div className="flex gap-2.5">
                  <DisabledTooltip
                    disabled={[BIM_ENGINE, CAD_ENGINE].includes(engineType) && !viewerLoaded}
                    tooltip={t("Please wait for model to load")}
                  >
                    <ButtonComponent
                      buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                      onClick={handleOpenCreateIssue}
                      disabled={[BIM_ENGINE, CAD_ENGINE].includes(engineType) && !viewerLoaded}
                    >
                      <Text>{t("Create New")}</Text>
                    </ButtonComponent>
                  </DisabledTooltip>
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={() => setShowFilterIssue(!showFilterIssue)}
                  >
                    <span className="flex items-center gap-2.5" data-testid="filterBtnDropdown">
                      <IconComponent name="Filter" width="1rem" height="1rem" />
                      <span data-testid="filterBtnCreateIssue">
                        <Text>{t("Filter")}</Text>
                      </span>
                      {Object.keys(filterPredicates).length > 0 && Object.keys(filterConditions).length > 0 && (
                        <span className="rounded-xl text-xs px-3 bg-yet_to_start_communication text-white">
                          {Object.keys(filterConditions).length}
                        </span>
                      )}
                    </span>
                  </ButtonComponent>
                  <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={handleSortClick}>
                    <span className="flex items-center gap-2.5" data-testid="sortBtnDropdown">
                      <img src="/assets/icons/svg/sort-icon.svg" className="w-3 h-3" alt="" />
                      <span className="text-sm" data-testid="sortBtnCreateIssue">
                        <Text>{t("Sort")}</Text>
                      </span>
                    </span>
                  </ButtonComponent>
                  {[BIM_ENGINE, CAD_ENGINE].includes(engineType) && (
                    <>
                      <ButtonComponent
                        buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                        onClick={handleImportBCFButtonClick}
                        disabled={disableImportButton}
                      >
                        <span className="flex items-center gap-2.5" data-testid="importBCF">
                          <IconComponent name="ImportExcel" width="1rem" height="1rem" />

                          <span className="pe-1" data-testid="importText">
                            <Text>{t("Import BCF")}</Text>
                          </span>
                        </span>
                      </ButtonComponent>
                      <input
                        ref={inputFileRef}
                        type="file"
                        accept=".bcf,.bcfzip"
                        id="bcfImport"
                        onChange={handleBcfImport}
                        className="hidden"
                      />
                    </>
                  )}
                  {[PDF_ENGINE].includes(engineType) && (
                    <ToolTipComponent
                      showTipPointer
                      position="bottom"
                      content={t("This will show markup of all issues.")}
                    >
                      <ToggleSwitchComponent
                        change={showPdfMarkupsHandler}
                        onChange={showPdfMarkupsHandler}
                        checked={viewPdfMarkupToggle}
                      />
                    </ToolTipComponent>
                  )}
                </div>
                <div
                  className="flex h-8 items-center justify-end text-sm font-semibold text-[#192048] cursor-pointer"
                  data-testid="issuesSearchButton"
                >
                  <IconComponent
                    name="SearchIcon"
                    onClick={() => {
                      setSearchIssue(true);
                    }}
                    width="1.25rem"
                    height="1.25rem"
                  />
                </div>
              </div>
            )}

            {Object.keys(selectedIssues).length > 0 && (
              <>
                {deleteEnabled && (
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={handleDeleteButton}
                    disabled={!selectedIssuesPermissions.delete}
                  >
                    <span className="flex items-center gap-2.5" data-testid="deleteButton">
                      <img src="/assets/icons/svg/mi_delete.svg" alt="" />
                      <span data-testid="deleteText">
                        <Text>{t("Delete")}</Text>
                      </span>
                    </span>
                  </ButtonComponent>
                )}
                {![PDF_ENGINE].includes(engineType) && (
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={handleExportButton}
                    disabled={!selectedIssuesPermissions.export}
                  >
                    <span className="flex items-center gap-2.5" data-testid="exportButton">
                      <img src="/assets/icons/svg/fontisto_export.svg" alt="" />
                      <span data-testid="exportText">
                        <Text>{t("Export")}</Text>
                      </span>
                    </span>
                  </ButtonComponent>
                )}
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                  onClick={handleExportToPdfButton}
                  disabled={!selectedIssuesPermissions.export}
                >
                  <span className="flex items-center gap-2.5" data-testid="exportToPdfButton">
                    <img src="/assets/icons/svg/fontisto_export.svg" alt="" />
                    <span data-testid="exportToPdfText">
                      <Text>{t("Export To Pdf")}</Text>
                    </span>
                  </span>
                </ButtonComponent>
              </>
            )}

            {searchIssue && (
              <>
                <span>
                  <AiOutlineLeft style={{ cursor: "pointer" }} onClick={handleResetSearch} />
                </span>
                <input className="e-input" onChange={handleSetSearchString} placeholder={t("Search For Issue")} />
              </>
            )}

            {/* {false && Object.keys(selectedIssues).length > 0 && (
              <span
                onClick={handleDelete}
                style={{ marginInlineStart: "1rem", cursor: "pointer" }}
              >
                <img
                  src={`${getImageUrl()}/delete.svg`}
                />
              </span>
            )} */}
          </div>

          {showFilterIssue && (
            <div className="flex flex-col gap-5 bg-[#f0f5f9] p-5">
              <div className="flex flex-row gap-4 bg-[#f0f5f9]">
                <div className="flex w-full flex-col gap-4" data-testid="leftPartFilterIssues">
                  <div
                    className="flex flex-row items-center text-sm text-[#192048]"
                    data-testid="issueTitleFilterIssues"
                  >
                    <div style={{ width: "100%" }} data-testid="issueTitleTextFilterIssues">
                      <Text>{t("Issue Title")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.title ?? ""}
                      name="title"
                      placeholder={t("Issue Title")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div className="flex flex-row items-center text-sm text-[#192048]" data-testid="statusFilterIssues">
                    <div style={{ width: "100%" }} data-testid="statusTextFilterIssues">
                      <Text>{t("Status")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.status ?? ""}
                      name="status"
                      placeholder={t("Status")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div
                    className="flex flex-row items-center text-sm text-[#192048]"
                    data-testid="createdByFilterIssues"
                  >
                    <div style={{ width: "100%" }}>
                      <Text>{t("Created by")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.createdByName ?? ""}
                      name="createdByName"
                      placeholder={t("Created by")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div className="flex flex-row items-center text-sm text-[#192048]" data-testid="versionFilterIssues">
                    <div style={{ width: "100%" }}>
                      <Text>{t("Version")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.fileVersionNumber ?? ""}
                      name="fileVersionNumber"
                      placeholder={t("Version")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                </div>
                <div className="flex w-full flex-col gap-4" data-testid="rightPartFilterIssues">
                  <div className="flex flex-row items-center text-sm text-[#192048]" data-testid="issueIDFilterIssues">
                    <div style={{ width: "100%" }} data-testid="issueIDTextFilterIssues">
                      <Text>{t("Issue ID")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.issueCounter ?? ""}
                      name="issueCounter"
                      placeholder={t("Issue ID")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div className="flex flex-row items-center text-sm text-[#192048]" data-testid="tagsFilterIssues">
                    <div style={{ width: "100%" }} data-testid="tagsTextFilterIssues">
                      <Text>{t("Tags")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.tagData ?? ""}
                      name="tagData"
                      placeholder={t("Tags")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  <div
                    className="flex flex-row items-center text-sm text-[#192048]"
                    data-testid="assignedToFilterIssues"
                  >
                    <div style={{ width: "100%" }} data-testid="assignedToTextFilterIssues">
                      <Text>{t("Assigned To")}</Text>
                    </div>
                    <TextFieldComponent
                      value={filterConditions.assignedToUserName ?? ""}
                      name="assignedToUserName"
                      placeholder={t("Assigned To")}
                      onChange={handleSetFilterConditions}
                    />
                  </div>
                  {/* <div className={css.filterParentInnerColelem}>
                  <div style={{ width: '100%' }}>{t("Type")}</div>
                  <input className="e-input" name="type" placeholder={t("Type")} onChange={handleSetFilterConditions} />
                </div> */}
                </div>
              </div>
              <div className="flex gap-5" data-testid="btnFilterIssues">
                <ButtonComponent
                  buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                  onClick={() => {
                    onInputChangePred();
                  }}
                >
                  <Text>{t("Apply")}</Text>
                </ButtonComponent>
                <ButtonComponent
                  buttonType={BUTTON_TYPES.BORDER_BUTTON}
                  onClick={() => {
                    setShowFilterIssue(!showFilterIssue);
                  }}
                >
                  <Text>{t("Cancel")}</Text>
                </ButtonComponent>
                <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={resetFilterHandler}>
                  <Text>{t("Reset")}</Text>
                </ButtonComponent>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        id="scroableIssues"
        className="h-auto w-full flex-1 flex-grow overflow-y-auto bg-white"
        style={{
          marginTop: HoopsModalStatus.Issues?.currentStage === "issues" ? "0.25rem" : "0",
        }}
      >
        {["issues"].includes(HoopsModalStatus.Issues?.currentStage ?? "") && (
          <Suspense fallback={<></>}>
            <IssuesList
              onViewIssueClick={onViewIssueClick}
              handleSelectedIssues={handleSelectedIssues}
              fileVersionId={fileVersionId}
              transmittalVersionId={transmittalVersionId}
              selectedIssues={selectedIssues}
              searchString={searchString}
              filterPredicates={filterPredicates}
              sortPredicates={sortPredicates}
              engineType={engineType}
            />
          </Suspense>
        )}
        {["view_issue"].includes(HoopsModalStatus.Issues?.currentStage ?? "") &&
          currentViewIssueId &&
          viewIssueSpaceId && (
            <Suspense fallback={<></>}>
              <ViewIssue
                currentViewIssueId={currentViewIssueId}
                selectedIssues={selectedIssues}
                fileVersionId={fileVersionId}
                handleSelectedIssues={handleSelectedIssues}
                fileType={fileType}
                engineType={engineType}
                spaceId={viewIssueSpaceId}
              />
            </Suspense>
          )}
        {["create_issue", "edit_issue"].includes(HoopsModalStatus.Issues?.currentStage ?? "") && (
          <Suspense fallback={<></>}>
            <CreateIssue
              fileVersionId={fileVersionId}
              transmittalVersionId={transmittalVersionId}
              fileType={fileType}
              engineType={engineType}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}

export default IssuesPallet;
